/**
 * ConfirmDeleteButton - Reusable delete button with confirmation
 * First click: enters confirmation mode (pulsing animation)
 * Second click: executes delete
 * Auto-resets after 3 seconds
 */

import React, { useState, useCallback } from 'react';
import { IconButton, IconButtonProps } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';

interface ConfirmDeleteButtonProps extends Omit<IconButtonProps, 'onClick' | 'icon' | 'aria-label'> {
  onDelete: () => void | Promise<void>;
  confirmTimeout?: number;
  iconSize?: number;
  'aria-label'?: string;
  onConfirmStateChange?: (isConfirming: boolean) => void;
}

export const ConfirmDeleteButton: React.FC<ConfirmDeleteButtonProps> = ({
  onDelete,
  confirmTimeout = 3000,
  iconSize = 12,
  'aria-label': ariaLabel,
  size = 'xs',
  onConfirmStateChange,
  ...rest
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirmDelete) {
      setConfirmDelete(true);
      onConfirmStateChange?.(true);
      setTimeout(() => {
        setConfirmDelete(false);
        onConfirmStateChange?.(false);
      }, confirmTimeout);
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
      onConfirmStateChange?.(false);
    }
  }, [confirmDelete, confirmTimeout, onDelete, onConfirmStateChange]);

  return (
    <IconButton
      aria-label={ariaLabel || (confirmDelete ? "Click again to confirm delete" : "Delete")}
      icon={<Trash2 size={iconSize} />}
      size={size}
      variant={confirmDelete ? 'solid' : 'ghost'}
      onClick={handleClick}
      colorScheme="red"
      isLoading={isDeleting}
      bg={confirmDelete ? 'red.500' : undefined}
      color={confirmDelete ? 'white' : undefined}
      _hover={{
        bg: confirmDelete ? 'red.600' : undefined,
        transform: confirmDelete ? 'scale(1.1)' : undefined,
      }}
      transition="all 0.2s"
      animation={confirmDelete ? 'pulse 0.5s ease-in-out infinite' : undefined}
      sx={{
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' },
        },
      }}
      {...rest}
    />
  );
};

export default ConfirmDeleteButton;
